'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { APP_USER_AGENT } from '@/components/common/AppPromoBanner/constants';

// Per-session marker. Set as soon as the banner renders (not on
// dismiss) so it only ever appears on the user's first qualifying
// page view of the session.
const SESSION_KEY = 'ots_app_banner_seen';

// Mirror the legacy smartbanner.js suppression: home page got
// `smartbanner:hide-path: /`, so home stays banner-free.
const SUPPRESS_PATHS = new Set<string>(['/']);

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.skireport';
const APP_ICON = 'https://images.onthesnow.com/images/apple-touch-icon.png';

// Android-only promo bar. iOS users get Safari's native
// `apple-itunes-app` smart banner (wired in _document.tsx); desktop
// and the in-app webview get nothing.
export const AppPromoBanner = () => {
  const pathname = usePathname() ?? '/';
  const { t } = useOTSTranslation('common');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    if (SUPPRESS_PATHS.has(pathname)) return;
    const ua = navigator.userAgent;
    if (ua.includes(APP_USER_AGENT)) return;
    if (!/Android/i.test(ua)) return;
    setShow(true);
    // Mark immediately — first qualifying view shows it, then it
    // gets out of the way for the rest of the session.
    sessionStorage.setItem(SESSION_KEY, '1');
  }, [pathname]);

  const dismiss = useCallback(() => setShow(false), []);

  if (!show) return null;

  return (
    <div className="flex items-center gap-3 border-b border-border bg-card px-3 py-2">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={dismiss}
        className="-ml-1 p-1 text-muted-foreground"
      >
        <X size={18} />
      </button>
      <img src={APP_ICON} alt="" className="h-10 w-10 rounded-md" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">
          {t('smartBanner.otsOnAndroid')}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {t('smartBanner.appTitle')}
        </div>
      </div>
      <a
        href={PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={dismiss}
        className="rounded-md px-3 py-1.5 text-sm font-semibold text-primary"
      >
        {t('smartBanner.ctaOpen')}
      </a>
    </div>
  );
};
